import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { storyElement } from "@/lib/story-element";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  User, Settings, LogOut, Loader2, Users, LogIn, Shield, Coins,
  Wallet, Send, TrendingDown, AlertCircle, Globe, Menu, ChevronDown,
  Bell, Info, UserPlus, Swords, CheckCheck, X,
  Compass, BookOpen, Map, CreditCard, Mail, MessageSquare, Inbox,
  Gamepad2, Pencil, FileText, ScrollText,
} from "lucide-react";
import logoSvg from "@/assets/logo.svg";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { useUserRole } from "@/hooks/useUserRole";
import { useNotifications } from "@/hooks/useNotifications";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLocale } from "@/hooks/useLocale";

interface Profile {
  full_name: string | null;
  avatar_url: string | null;
}

const NOTIF_TYPE_ICONS: Record<string, React.ElementType> = {
  friend_request: UserPlus,
  turn_alert: Swords,
  info: Info,
};

function timeAgo(dateStr: string, t: { forum_time_just_now: string; forum_time_minutes_ago: string; forum_time_hours_ago: string; forum_time_days_ago: string }): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return t.forum_time_just_now;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}${t.forum_time_minutes_ago}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}${t.forum_time_hours_ago}`;
  const days = Math.floor(hours / 24);
  return `${days}${t.forum_time_days_ago}`;
}

/**
 * Minimal gameplay header for the V2 Adventure Flow.
 * Top bar shows only the logo (left) and a hamburger (right). The hamburger opens a
 * right-side Sheet:
 *   • Gameplay menu — shown when on a play/adventure page (world edit, adventure settings)
 *   • Site menu — nav links, open by default, fills the top.
 *   • Account (avatar) — pinned to the bottom; expands to profile links, a collapsible
 *     Credits sub-menu and a Notifications popup. Language lives in profile settings only.
 */
const GameplayHeader = ({
  worldId,
  adventureId,
  isWorldOwner = false,
  isAdmin = false,
}: {
  worldId?: string | null;
  adventureId?: string | null;
  isWorldOwner?: boolean;
  isAdmin?: boolean;
}) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin } = useUserRole();
  const { balance: creditBalance, thresholds: creditThresholds } = useCreditBalance();
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotification } = useNotifications();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Sheet + collapsible state. Site menu is OPEN by default; the inner sections are closed.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [gameplayOpen, setGameplayOpen] = useState(true);
  const [siteMenuOpen, setSiteMenuOpen] = useState(true);
  const [accountOpen, setAccountOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const user = await getSessionUser();
        if (!user) { setIsLoading(false); setIsLoggedIn(false); return; }
        setIsLoggedIn(true);
        setUserEmail(user.email ?? null);
        const { data, error } = await supabase
          .from("profiles")
          .select("full_name, avatar_url")
          .eq("id", user.id)
          .single();
        if (error) console.error("Error fetching profile:", error);
        else setProfile(data);
      } catch (error) {
        console.error("Error in fetchProfile:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error(t.sign_out_failed, { description: error.message });
    } else {
      navigate("/auth");
    }
  };

  const getInitials = () => {
    if (profile?.full_name) {
      return profile.full_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
    }
    return userEmail?.[0]?.toUpperCase() || "U";
  };

  const go = (path: string) => {
    setSheetOpen(false);
    navigate(path);
  };

  const isActive = (path: string) => location.pathname === path;

  const navLinks: { label: string; path: string; icon?: React.ElementType }[] = [
    { label: t.nav_worlds, path: "/", icon: Globe },
    { label: t.nav_explore, path: "/explore", icon: Compass },
    { label: t.nav_about, path: "/about", icon: BookOpen },
    { label: t.nav_roadmap, path: "/roadmap", icon: Map },
    { label: t.nav_pricing, path: "/support", icon: CreditCard },
    { label: t.nav_contact, path: "/contact", icon: Mail },
    { label: t.nav_forums, path: "/community", icon: MessageSquare },
    { label: t.nav_messages, path: "/messages", icon: Inbox },
  ];

  const rowCls = "w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left";
  const subRowCls = "w-full flex items-center gap-2.5 px-3 py-2 text-xs rounded-md hover:bg-accent transition-colors text-left";

  return (
    <header {...storyElement("gameplay-header")} className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 w-full items-center px-4">
        {/* Left: logo */}
        <div
          onClick={() => navigate("/")}
          className="flex items-center gap-2 cursor-pointer px-2"
        >
          <img src={logoSvg} alt="Adventure Venture" style={{ width: 50, height: 45 }} />
          <span className="hidden sm:flex flex-col leading-none font-bold text-xs tracking-tight">
            <span className="ml-0">ADVENTURE</span>
            <span className="ml-2">VENTURE</span>
          </span>
        </div>

        <div className="flex-1" />

        {/* Right: hamburger */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={t.gh_open_menu} className="h-9 w-9 relative">
              <Menu className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground ring-2 ring-background">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full max-w-sm p-0 flex flex-col gap-0">
            <SheetHeader className="px-4 py-3 border-b border-border/40 text-left">
              <SheetTitle>{t.gh_menu}</SheetTitle>
            </SheetHeader>

            {/* ── Gameplay menu (only when on a play page with access) ── */}
            {(worldId || adventureId) && (isWorldOwner || isAdmin) && (
              <div className="border-b border-border/40 p-2">
                <button
                  className={cn(rowCls, "font-medium justify-between text-primary")}
                  onClick={() => setGameplayOpen((v) => !v)}
                  aria-expanded={gameplayOpen}
                >
                  <span className="flex items-center gap-2.5">
                    <Gamepad2 className="w-4 h-4 text-primary" />
                    {"Gameplay"}
                  </span>
                  <ChevronDown className={cn("w-4 h-4 opacity-60 transition-transform", gameplayOpen && "rotate-180")} />
                </button>
                {gameplayOpen && (
                  <div className="pl-2 mt-0.5 space-y-0.5">
                    {worldId && adventureId && (
                      <>
                        <button
                          onClick={() => go(`/world/${worldId}?adventure=${adventureId}`)}
                          className={rowCls}
                        >
                          <Pencil className="w-4 h-4 text-muted-foreground" />
                          Editor
                        </button>
                        <button
                          onClick={() => go(`/play/${adventureId}`)}
                          className={cn(rowCls, "bg-accent text-accent-foreground")}
                        >
                          <Gamepad2 className="w-4 h-4 text-primary" />
                          Play
                        </button>
                        <div className="h-px bg-border/40 my-1" />
                      </>
                    )}
                    {worldId && (
                      <button
                        onClick={() => go(`/world/${worldId}`)}
                        className={rowCls}
                      >
                        <ScrollText className="w-4 h-4 text-muted-foreground" />
                        Edit World Template
                      </button>
                    )}
                    {adventureId && (
                      <button
                        onClick={() => go(`/play/${adventureId}?settings=1`)}
                        className={rowCls}
                      >
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        Edit Adventure Instance
                      </button>
                    )}
                    {worldId && (
                      <button
                        onClick={() => go(`/world/${worldId}?tab=design`)}
                        className={rowCls}
                      >
                        <Pencil className="w-4 h-4 text-muted-foreground" />
                        Design World
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Site menu (open by default, fills the top) ── */}
            <div className="flex-1 overflow-y-auto p-2">
              <button
                className={cn(rowCls, "font-medium justify-between")}
                onClick={() => setSiteMenuOpen((v) => !v)}
                aria-expanded={siteMenuOpen}
              >
                <span className="flex items-center gap-2.5">
                  <Menu className="w-4 h-4 text-muted-foreground" />
                  {t.gh_menu}
                </span>
                <ChevronDown className={cn("w-4 h-4 opacity-60 transition-transform", siteMenuOpen && "rotate-180")} />
              </button>
              {siteMenuOpen && (
                <div className="pl-2 mt-0.5 space-y-0.5">
                  {navLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <button
                        key={link.path}
                        onClick={() => go(link.path)}
                        className={cn(rowCls, isActive(link.path) && "bg-accent text-accent-foreground")}
                      >
                        {Icon ? <Icon className="w-4 h-4 text-muted-foreground" /> : <span className="w-4" />}
                        {link.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Account / avatar (pinned to the bottom) ── */}
            <div className="border-t border-border/40 p-2">
              {/* Expanded account items render ABOVE the avatar trigger */}
              {accountOpen && (
                <div className="mb-1 max-h-[55vh] overflow-y-auto space-y-0.5">
                  {!isLoggedIn && !isLoading ? (
                    <button className={rowCls} onClick={() => go("/auth")}>
                      <LogIn className="w-4 h-4 text-muted-foreground" />
                      {t.nav_sign_in_up}
                    </button>
                  ) : isLoggedIn ? (
                    <>
                      <button className={rowCls} onClick={() => go("/profile")}>
                        <User className="w-4 h-4 text-muted-foreground" />
                        {t.profile}
                      </button>
                      <button className={rowCls} onClick={() => go("/settings")}>
                        <Settings className="w-4 h-4 text-muted-foreground" />
                        {t.settings}
                      </button>
                      {isSuperAdmin && (
                        <button className={rowCls} onClick={() => go("/admin/users")}>
                          <Shield className="w-4 h-4 text-muted-foreground" />
                          {t.nav_user_management}
                        </button>
                      )}
                      <button className={rowCls} onClick={() => go("/profile?tab=friends")}>
                        <Users className="w-4 h-4 text-muted-foreground" />
                        {t.profile_friends}
                      </button>

                      <div className="h-px bg-border/40 my-1" />

                      {/* Credits — collapsible sub-menu, closed by default */}
                      {creditBalance !== null && (
                        <div>
                          <button
                            className={cn(rowCls, "justify-between")}
                            onClick={() => setCreditsOpen((v) => !v)}
                            aria-expanded={creditsOpen}
                          >
                            <span className="flex items-center gap-2.5">
                              <Coins className={cn("w-4 h-4", !creditThresholds.canUseCredits ? "text-destructive" : "text-muted-foreground")} />
                              {t.gh_credits}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <span className={cn(
                                "text-xs font-mono tabular-nums",
                                !creditThresholds.canUseCredits && "text-destructive",
                                creditThresholds.canUseCredits && !creditThresholds.canBuild && "text-orange-500",
                              )}>
                                {creditBalance.toFixed(2)}
                              </span>
                              <ChevronDown className={cn("w-4 h-4 opacity-60 transition-transform", creditsOpen && "rotate-180")} />
                            </span>
                          </button>
                          {creditsOpen && (
                            <div className="pl-2 mt-0.5 space-y-0.5">
                              {!creditThresholds.canUseCredits && (
                                <div className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-destructive">
                                  <AlertCircle className="w-3 h-3 shrink-0" />
                                  <span>{t.hdr_ai_disabled}</span>
                                </div>
                              )}
                              {creditThresholds.canUseCredits && !creditThresholds.canBuild && (
                                <div className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-orange-500">
                                  <AlertCircle className="w-3 h-3 shrink-0" />
                                  <span>{t.hdr_building_disabled}</span>
                                </div>
                              )}
                              <button className={subRowCls} onClick={() => go("/settings?tab=credits")}>
                                <Wallet className="w-3.5 h-3.5 text-muted-foreground" />
                                {t.hdr_credit_wallet}
                              </button>
                              <button className={subRowCls} onClick={() => go("/profile?tab=credits")}>
                                <TrendingDown className="w-3.5 h-3.5 text-muted-foreground" />
                                {t.hdr_usage_dashboard}
                              </button>
                              <button className={subRowCls} onClick={() => go("/settings?tab=pricing")}>
                                <Coins className="w-3.5 h-3.5 text-muted-foreground" />
                                {t.hdr_pricing_rates}
                              </button>
                              <button className={subRowCls} onClick={() => go("/settings?tab=referrals")}>
                                <Send className="w-3.5 h-3.5 text-muted-foreground" />
                                {t.hdr_earn_credits}
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="h-px bg-border/40 my-1" />

                      {/* Notifications — opens as a popup (Popover), not an inline sub-menu */}
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className={cn(rowCls, "justify-between")}>
                            <span className="flex items-center gap-2.5">
                              <Bell className="w-4 h-4 text-muted-foreground" />
                              {t.aria_notifications}
                            </span>
                            {unreadCount > 0 && (
                              <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground">
                                {unreadCount > 9 ? "9+" : unreadCount}
                              </span>
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" side="left" className="w-80 p-0">
                          <div className="flex items-center justify-between px-3 py-2 border-b border-border/40">
                            <span className="text-sm font-medium">{t.aria_notifications}</span>
                            {unreadCount > 0 && (
                              <button
                                onClick={() => markAllAsRead()}
                                className="text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                              >
                                <CheckCheck className="w-3 h-3" /> {t.notif_mark_all_read}
                              </button>
                            )}
                          </div>
                          {notifications.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">{t.notif_empty}</div>
                          ) : (
                            <div className="max-h-72 overflow-y-auto">
                              {notifications.map((n) => {
                                const Icon = NOTIF_TYPE_ICONS[n.type] || Info;
                                return (
                                  <div
                                    key={n.id}
                                    className={cn(
                                      "flex items-start gap-3 px-3 py-2.5 hover:bg-accent/50 transition-colors",
                                      !n.read_at && "bg-primary/5",
                                    )}
                                  >
                                    <button
                                      className="flex items-start gap-3 flex-1 min-w-0 text-left"
                                      onClick={() => {
                                        if (!n.read_at) markAsRead(n.id);
                                        if (n.link && n.link.startsWith("/")) go(n.link);
                                      }}
                                    >
                                      <Icon className={cn("w-4 h-4 mt-0.5 shrink-0", !n.read_at ? "text-primary" : "text-muted-foreground")} />
                                      <span className="flex-1 min-w-0">
                                        <span className={cn("block text-sm", !n.read_at && "font-medium")}>{n.title}</span>
                                        {n.body && <span className="block text-xs text-muted-foreground truncate">{n.body}</span>}
                                        <span className="block text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at, t)}</span>
                                      </span>
                                    </button>
                                    <button
                                      onClick={() => clearNotification(n.id)}
                                      className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
                                      aria-label={t.nav_clear}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>

                      <div className="h-px bg-border/40 my-1" />

                      <button className={cn(rowCls, "text-destructive")} onClick={handleSignOut}>
                        <LogOut className="w-4 h-4" />
                        {t.nav_sign_out}
                      </button>
                    </>
                  ) : null}
                </div>
              )}

              {/* Avatar trigger row */}
              <button
                className={cn(rowCls, "justify-between")}
                onClick={() => setAccountOpen((v) => !v)}
                aria-expanded={accountOpen}
              >
                <span className="flex items-center gap-3 min-w-0">
                  {isLoading ? (
                    <span className="flex h-9 w-9 items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </span>
                  ) : isLoggedIn ? (
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.full_name || "User"} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-muted text-muted-foreground text-xs">
                        <User className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <span className="flex flex-col min-w-0">
                    <span className="text-sm font-medium leading-none truncate">
                      {isLoggedIn ? (profile?.full_name || t.nav_set_display_name) : t.nav_guest_browsing}
                    </span>
                    {isLoggedIn && userEmail && (
                      <span className="text-xs leading-none text-muted-foreground truncate mt-1">{userEmail}</span>
                    )}
                  </span>
                </span>
                <ChevronDown className={cn("w-4 h-4 opacity-60 shrink-0 transition-transform", accountOpen && "rotate-180")} />
              </button>
            </div>

            <SheetClose className="sr-only">{t.gh_open_menu}</SheetClose>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default GameplayHeader;
